import config from "appConfig";

import lesson0414 from "../../course-04/lesson-14/lesson";

import { getLinkForCourseLesson } from "../../../../services/Utils";

const linkLesson0414 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0414.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>Sound sources radiate sound energy in all directions in space. Their directivity patterns can be very complex, especially at medium and high frequencies. However, at low frequencies, or for relatively simple sources, the radiation can be accurately approximated from elementary acoustic sources such as the monopole, the dipole or the baffled planar piston. All these sources and their main characteristics will be presented in this Lesson.
<h3 class=sectionHead>Monopole Source</h3>
<p>
   For a harmonic signal, the pressure radiated by a monopole source, also called a point source, can be calculated by solving the wave equation in free space. The pressure at a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ωt</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>kr</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   is volumetric mass density of air,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   is the angular frequency,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   is the flow rate of the monopole and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   is the distance between the source and the point of interest (see <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-1.svg data-img-alt="Figure 1. Monopole source.">Figure 1</a>). For saving space, the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jωt</mi>
         </mrow>
      </msup>
   </math>
   factor will be omitted throughout this Lesson. Strictly speaking, as the point source is infinitesimally small it has a zero surface area and therefore a zero flow rate. It can however be assimilated to a radially pulsating sphere of radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
   </math>
   (as long as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <mo class=MathClass-rel>≪</mo>
      <mi>λ</mi>
   </math>) of non-zero flow rate
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 1. Monopole source." src=${config.ASSETS_URL}/04/06/theory/figure-1.svg><figcaption class=caption>Figure 1. Monopole source.</figcaption></figure>
<p>The main properties of monopole sources are:
<ul class=itemize1>
   <li class=itemize>
      the equation giving the pressure is independent of the angles
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>𝜃</mi>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>ϕ</mi>
      </math>. The radiation is therefore equal for all angles: The monopole is an <strong><span class=cmbx-10>omnidirectional</span></strong> source,
   <li class=itemize>
      the radial dependence is in
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
         <mo class=MathClass-bin>∕</mo>
         <mi>r</mi>
      </math>
      which means that the sound pressure level loses
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>6</mn>
      </math>
      dB each time the distance doubles,
   <li class=itemize>
      the pressure is proportional to the frequency (included in the
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>ω</mi>
         <mo class=MathClass-rel>=</mo>
         <mn>2</mn>
         <mi mathvariant=italic>πf</mi>
      </math>
      term). To radiate a constant pressure level as a function of frequency, the source flow rate has to decrease (proportionally) with a
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
         <mo class=MathClass-bin>∕</mo>
         <mi>f</mi>
      </math>
      law in order to keep a constant
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi mathvariant=italic>ωQ</mi>
      </math>
      term. This is usually achieved by using a diaphragm above its resonance frequency, its velocity decreases in
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
         <mo class=MathClass-bin>∕</mo>
         <mi>f</mi>
      </math>
      and its acceleration is therefore constant. We will come back to this point when we study the radiation from loudspeakers.
</ul>
<h3 class=sectionHead>Dipole source</h3>
<p>
   A dipole source consists of two monopoles with opposite phase separated by a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
   </math>
   (see <a data-img-src=${config.ASSETS_URL}/04/06/theory/figure-2.svg data-img-alt="Figure 2. Dipole source." href=#>Figure 2</a>).
   <figure class=figure>
<p><img alt="Figure 2. Dipole source." src=${config.ASSETS_URL}/04/06/theory/figure-2.svg><figcaption class=caption>Figure 2. Dipole source.</figcaption></figure>
<p>
   As the two monopoles are located on the axis
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi>
   </math>, the radiated pressure will be axisymmetric with no dependence on the angle
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>. The 2D problem is redrawn in <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-3.svg data-img-alt="Figure 3. Dipole source, 2D geometry.">Figure 3</a>. The pressure at a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   from the dipole centre is given by
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi mathvariant=italic>ωQ</mi>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <msub>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>1</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>−</mo>
                              <mi mathvariant=italic>jk</mi>
                              <msub>
                                 <mrow>
                                    <mi>r</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                        <mi>π</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   For large distances (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>d</mi>
   </math>), the path difference
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mo class=MathClass-rel>≃</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜗</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. Then, using a first order approximation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>δ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>δ</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>.
   <figure class=figure>
<p><img alt="Figure 3. Dipole source, 2D geometry." src=${config.ASSETS_URL}/04/06/theory/figure-3.svg><figcaption class=caption>Figure 3. Dipole source, 2D geometry.</figcaption></figure>
<p>Since the distance is large, it is possible:
<ul class=itemize1>
   <li class=itemize>
      to perform a zero-order approximation for the amplitude term as the function
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
         <mo class=MathClass-bin>∕</mo>
         <mi>r</mi>
      </math>
      decreases slowly for large arguments:
      <table class=equation>
         <tr>
            <td>
               <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-rel>≃</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-rel>≃</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-punc></mo>
               </math>
            <td class=eq-no>(3)
      </table>
   <li class=itemize>
      and to keep the first order approximation for the phase term (as the complex exponential can oscillate rapidly):
</ul>
<table class=equation>
         <tr>
            <td style="font-size: 8px;">
               <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
                  <mo class=MathClass-rel>≃</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jk</mi>
                        <mfrac>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </mfrac>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                     </mrow>
                  </msup>
                  <mstyle class=text>
                     <mtext> and </mtext>
                  </mstyle>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <msub>
                           <mrow>
                              <mi>r</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </msup>
                  <mo class=MathClass-rel>≃</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mfrac>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </mfrac>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-punc></mo>
               </math>
            <td class=eq-no>(4)
      </table>
<p>These approximations lead to the following expression for pressure radiated by a dipole source
<table class=equation>
   <tr>
      <td style="font-size: 4px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jk</mi>
                        <mfrac>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </mfrac>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mfrac>
                           <mrow>
                              <mi>d</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </mfrac>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                        <mi>𝜃</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kd</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>Then, the amplitude term is equal to
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>kd</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-rel>=</mo>
            <mn>2</mn>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>|</mo>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>kd</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   At low frequencies, <em><span class=cmti-10>i.e.</span></em>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>d</mi>
      <mo class=MathClass-rel>≪</mo>
      <mi>λ</mi>
   </math>, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>kd</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>2</mn>
            <mi mathvariant=italic>πd</mi>
         </mrow>
         <mrow>
            <mi>λ</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>≪</mo>
      <mn>1</mn>
   </math>
   and then
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>kd</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
      <mo class=MathClass-rel>≃</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>kd</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>. The amplitude term can then be rewritten
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mi mathvariant=italic>ωkdQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πr</mi>
               </mrow>
            </mfrac>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi mathvariant=italic>dQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>πrc</mi>
               </mrow>
            </mfrac>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>The main properties of the pressure radiated by a dipole sources are:
<ul class=itemize1>
   <li class=itemize>
      the angle dependence of the dipole source at low frequencies is proportional to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi class=qopname>cos</mi>
         <mo>⁡</mo>
         <mi>𝜃</mi>
      </math>. It is then a <strong><span class=cmbx-10>bidirectional</span></strong> source: it has two maximum values for
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>𝜃</mi>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>∘</mo>
            </mrow>
         </msup>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>18</mn>
         <msup>
            <mrow>
               <mn>0</mn>
            </mrow>
            <mrow>
               <mo class=MathClass-bin>∘</mo>
            </mrow>
         </msup>
      </math>. The directivity response of monopole and dipole sources is plotted in the upper part of <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-4.svg data-img-alt="Figure 4. Monopole and dipole angle responses.">Figure 4</a>,
   <li class=itemize>
      as for the monopole, the distance dependency is specified by the term
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
         <mo class=MathClass-bin>∕</mo>
         <mi>r</mi>
      </math>,
   <li class=itemize>the pressure is proportional to the separation distance between the two sources,
   <li class=itemize>
      the frequency dependence is
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo class=MathClass-rel>∝</mo>
         <msup>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </math>
      at low frequencies and then alternates with a series of minima and maxima that are due to constructive or destructive interference between the two sources. This behaviour is shown in <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-5.svg data-img-alt="Figure 5. Monopole and dipole frequency responses for a constant input flow rate.">Figure 5</a> which plots the frequency responses of monopole and dipole sources for a constant input flow rate
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>Q</mi>
      </math>.
</ul>
<figure class=figure>
   <img alt="Figure 4. Monopole and dipole angle responses." src=${config.ASSETS_URL}/04/06/theory/figure-4.svg>
   <figcaption class=caption>Figure 4. Monopole and dipole angle responses.</figcaption>
</figure>
<figure class=figure>
   <p>
      <img alt="Figure 5. Monopole and dipole frequency responses for a constant input flow rate." src=${config.ASSETS_URL}/04/06/theory/figure-5.svg>
      <figcaption class=caption>Figure 5. Monopole and dipole frequency responses for a constant input flow rate.</figcaption>
</figure>
<p>A typical device that exhibits this directivity pattern is the pressure gradient microphone (also known as bidirectional or figure of 8 microphone), which will be described in <a href=${linkLesson0414} target="_blank">Lesson 14</a> of this course.
<h3 class=sectionHead>Baffled piston</h3>
<p>
   A circular piston of radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
   </math>, mounted in an infinite rigid baffle (see <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-6.svg data-img-alt="Figure 6. Circular piston in a infinite rigid baffle.">Figure 6</a>), is vibrating with a uniform normal velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>. The pressure radiated at position
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   can be calculated by using the Rayleigh integral
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mstyle mathvariant=bold>
               <mi>r</mi>
            </mstyle>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mo>∬</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mo class=MathClass-rel>|</mo>
                        <mstyle mathvariant=bold>
                           <mi>r</mi>
                        </mstyle>
                        <mo class=MathClass-bin>−</mo>
                        <mstyle mathvariant=bold>
                           <msub>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                        </mstyle>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mo class=MathClass-rel>|</mo>
                  <mstyle mathvariant=bold>
                     <mi>r</mi>
                  </mstyle>
                  <mo class=MathClass-bin>−</mo>
                  <mstyle mathvariant=bold>
                     <msub>
                        <mrow>
                           <mi>r</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mstyle>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mover accent=true>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mo accent=true>→</mo>
            </mover>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dS</mi>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>π</mi>
      <msup>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   is the piston surface,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <mi>r</mi>
      </mstyle>
   </math>
   is a point in the half space
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Z</mi>
      <mo class=MathClass-rel>≥</mo>
      <mn>0</mn>
   </math>, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi>r</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </mstyle>
   </math>
   is a point on the piston. Note that the Rayleigh integral is normally calculated over the entire baffle, but since the velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   is null outside of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>, this integration is therefore limited to the piston surface.<br class=newline>Note that this integral can be seen as the sum of identical monopole contribution distributed continuously over the surface of the piston. The term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mi>π</mi>
   </math>
   instead of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>4</mn>
      <mi>π</mi>
   </math>
   (in the original formulation of the Rayleigh integral) reflects into the baffled condition: the sound is radiated into a half space instead of a full space.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 6. Circular piston in a infinite rigid baffle." src=${config.ASSETS_URL}/04/06/theory/figure-6.svg><figcaption class=caption>Figure 6. Circular piston in a infinite rigid baffle.</figcaption></figure>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mover accent=true>
         <mrow>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
      <mo class=MathClass-close>)</mo>
   </math>
   is uniform on the piston (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mover accent=true>
         <mrow>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>V</mi>
   </math>), the Rayleigh integral is simplified to
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mstyle mathvariant=bold>
               <mi>r</mi>
            </mstyle>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi mathvariant=italic>ωV</mi>
            <msub>
               <mrow>
                  <mo>∬</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msub>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jk</mi>
                        <mo class=MathClass-rel>|</mo>
                        <mstyle mathvariant=bold>
                           <mi>r</mi>
                        </mstyle>
                        <mo class=MathClass-bin>−</mo>
                        <mstyle mathvariant=bold>
                           <msub>
                              <mrow>
                                 <mi>r</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                        </mstyle>
                        <mo class=MathClass-rel>|</mo>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mo class=MathClass-rel>|</mo>
                  <mstyle mathvariant=bold>
                     <mi>r</mi>
                  </mstyle>
                  <mo class=MathClass-bin>−</mo>
                  <mstyle mathvariant=bold>
                     <msub>
                        <mrow>
                           <mi>r</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mstyle>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
            </mfrac>
            <mi mathvariant=italic>dS</mi>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   Note that the term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>jωV</mi>
   </math>
   represents the acceleration of the piston, so the radiated pressure is proportional to it. Solving the above equation allows the calculation of the on-axis pressure at large distance (<em><span class=cmti-10>i.e.</span></em> for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>a</mi>
   </math>, so
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <mstyle mathvariant=bold>
         <mi>r</mi>
      </mstyle>
      <mo class=MathClass-bin>−</mo>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi>r</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
      </mstyle>
      <mo class=MathClass-rel>|</mo>
   </math>
   has almost a constant value and can be taken out of the integral):
</p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <msub>
      <mrow>
         <mi>p</mi>
      </mrow>
      <mrow>
         <mi>p</mi>
      </mrow>
   </msub>
   <mo class=MathClass-open>(</mo>
   <mi>r</mi>
   <mo class=MathClass-punc>,</mo>
   <msup>
      <mrow>
         <mn>0</mn>
      </mrow>
      <mrow>
         <mo class=MathClass-bin>∘</mo>
      </mrow>
   </msup>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <mi>j</mi>
         <msub>
            <mrow>
               <mi>ρ</mi>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msub>
         <mi mathvariant=italic>ωQ</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
         <mi mathvariant=italic>πr</mi>
      </mrow>
   </mfrac>
   <msup>
      <mrow>
         <mi>e</mi>
      </mrow>
      <mrow>
         <mo class=MathClass-bin>−</mo>
         <mi mathvariant=italic>jkr</mi>
      </mrow>
   </msup>
   <mo class=MathClass-punc></mo>
</math>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi>V</mi>
   </math>
   is the piston flow rate. It can be seen that within a factor of 2 the radiated pressure at a large distance along the axis of a baffled planar piston has the same mathematical form as that of a monopole. The differences appear off axis where the pressure is given by the following equation
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <msup>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∘</mo>
               </mrow>
            </msup>
            <mo class=MathClass-close>)</mo>
            <mfrac>
               <mrow>
                  <mn>2</mn>
                  <msub>
                     <mrow>
                        <mi>J</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>ka</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ka</mi>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>𝜃</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the cylindrical Bessel function of first kind. The angular dependence for various values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ka</mi>
   </math>
   is plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-7.svg data-img-alt="Figure 7. Angular dependence of the baffled planar piston for various values of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>ka</mi></math>.">Figure 7</a>. The baffled piston is omnidirectional at low frequencies and becomes more and more directional as the frequency increases. Secondary lobes appear when the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ka</mi>
   </math>
   factor becomes greater than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>83</mn>
   </math>. For example, for a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>10</mn>
   </math>
    cm diameter piston (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>a</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>5</mn>
   </math>
    cm), this corresponds to a frequency of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>83</mn>
      <mi>c</mi>
      <mo class=MathClass-bin>∕</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi mathvariant=italic>πa</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>4169</mn>
   </math>
    Hz.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 7. Angular dependence of the baffled planar piston for various values of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>ka</mi></math>." src=${config.ASSETS_URL}/04/06/theory/figure-7.svg><figcaption class=caption>Figure 7. Angular dependence of the baffled planar piston for various values of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi mathvariant=italic>ka</mi></math>.</figcaption></figure>
<p>
   The radiation impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   is defined as the ratio of the potential and flow quantities at the surface of the piston:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>p</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-bin>∕</mo>
      <mi>Q</mi>
   </math>. The radiation impedance reflects the reaction of the fluid to an excitation (in the same way as an electrical impedance reflects the reaction of a circuit to an excitation in the form of a resistance and a reactance). It comprises, in its imaginary part, a mass term which reflects the fact that part of the air vibrates with the piston and a real part which reflects the active radiated power (the one which is radiated away from the object).<br class=newline>At low frequencies,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   has an approximate solution which is given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ar</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   respectively represent the acoustic resistance and mass of the radiation impedance and are given by
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mtable equalcolumns=false style="" equalrows=false class=array align=axis>
               <mtr>
                  <mtd class=array columnalign=left>
                     <msub>
                        <mrow>
                           <mi>R</mi>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>ar</mi>
                        </mrow>
                     </msub>
                     <mo class=MathClass-rel>=</mo>
                     <mfrac>
                        <mrow>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>c</mi>
                              </mrow>
                           </msub>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mrow>
                     </mfrac>
                     <msup>
                        <mrow>
                           <mo class=MathClass-open>(</mo>
                           <mi mathvariant=italic>ka</mi>
                           <mo class=MathClass-close>)</mo>
                        </mrow>
                        <mrow>
                           <mn>2</mn>
                        </mrow>
                     </msup>
                     <mo class=MathClass-punc></mo>
                  </mtd>
               </mtr>
               <mtr>
                  <mtd class=array columnalign=left>
                     <msub>
                        <mrow>
                           <mi>M</mi>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>ar</mi>
                        </mrow>
                     </msub>
                     <mo class=MathClass-rel>=</mo>
                     <mfrac>
                        <mrow>
                           <mn>8</mn>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <mi>a</mi>
                        </mrow>
                        <mrow>
                           <mn>3</mn>
                           <mi>π</mi>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mrow>
                     </mfrac>
                     <mo class=MathClass-rel>≃</mo>
                     <mfrac>
                        <mrow>
                           <mn>0</mn>
                           <mo class=MathClass-punc>.</mo>
                           <mn>85</mn>
                           <msub>
                              <mrow>
                                 <mi>ρ</mi>
                              </mrow>
                              <mrow>
                                 <mn>0</mn>
                              </mrow>
                           </msub>
                           <mi>a</mi>
                        </mrow>
                        <mrow>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mrow>
                     </mfrac>
                     <mo class=MathClass-punc></mo>
                  </mtd>
               </mtr>
            </mtable>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   One can notice that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   is not a “real” resistance as its value is proportional to the squared frequency. The validity of the low frequency formulation (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>λ</mi>
      <mo class=MathClass-rel>≫</mo>
      <mi>a</mi>
   </math>) is highlighted in <a href=# data-img-src=${config.ASSETS_URL}/04/06/theory/figure-8.svg data-img-alt="Figure 8. Radiation impedance of a baffled piston with and without approximation.">Figure 8</a> where a comparison between the formulation with no approximation is plotted. It can be seen that as long as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ka</mi>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>, the approximation is acceptable. It can also be seen that for high values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>ka</mi>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   becomes a constant value while
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ar</mi>
         </mrow>
      </msub>
   </math>
   decreases.
   <figure class=figure>
<p><img alt="Figure 8. Radiation impedance of a baffled piston with and without approximation." src=${config.ASSETS_URL}/04/06/theory/figure-8.svg><figcaption class=caption>Figure 8. Radiation impedance of a baffled piston with and without approximation.</figcaption></figure>
<h3 class=sectionHead>Usefulness of elementary sources</h3>
<p>A question naturally arises: do the elemental sources described above exist in real life? And by extension, what is the usefulness of the formalisms presented. Even if point sources like monopoles and dipoles do not really exist, they can approximate the behaviour of real sources under certain conditions. These conditions generally imply:
<ul class=itemize1>
   <li class=itemize>
      large distances: real sources have non-zero dimensions which must be small compared to the distance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>r</mi>
      </math>
      for which the pressure is calculated,
   <li class=itemize>bandwidth-limited behaviour: the dimensions of the source must be small compared to the wavelength of interest.
</ul>
<p>In these cases, the low-frequency radiation of a loudspeaker driver mounted in a closed enclosure can be assimilated to a monopole, whereas that of an unmounted loudspeaker driver will correspond to that of a dipole (the front and rear faces of the membrane produce opposite phase flows). At higher frequencies, the baffled plane piston model can be used to calculate the radiated pressure on- or off-axis of a loudspeaker mounted on a cabinet. Future Lessons in this electroacoustics course will allow the calculation of the acoustic flow produced by the diaphragm of a loudspeaker as a function of its physical parameters.<br class=newline>At higher frequencies, combinations of large numbers of monopoles and/or dipoles can also be used to express more complex physical phenomena such as the higher modes of membranes and the diffraction on the enclosure box. This method is called multipole expansion.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, the equations for calculating the pressure radiated by monopole or dipole sources, or a baffled plane piston were presented. The usefulness and limitations of these formulations were also discussed. This knowledge is important when modelling the radiation from acoustic sources and will be used in the Lessons of this course, especially those on loudspeaker enclosures.
<h3 class=likesectionHead>References</h3>
<dl class="thebibliography">
    <dt id=x1-7002x1>[1]</dt>
    <dd id="bib-1">
        <p>L. Beranek & T. Mellow, <em><span class=cmti-10>Acoustics: sound fields and transducers</span></em>, Academic Press, 2012.</p>
    </dd>
    <dt id=x1-7004x2>[2]</dt>
    <dd id="bib-2">
        <p>Brian Anderson, <em><span class=cmti-10>Understanding radiation impedance through animations</span></em>, Proc. Mtgs. Acoust. 33, (2018).</p>
    </dd>
</dl>`;

export default theory;
